import { useMemo, useState, useRef } from 'react';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { useGetOrganizationUsers } from 'hooks/useGetOrganizationUsers';
import { TimelineAssignee } from 'types/timeline';
import { USER_CLASSES, TIMELINE_ASSIGNEE_DEAL_ROLES } from 'services/constants';
import type { IUser } from 'types/user';
import RenderIf from 'components/RenderIf/RenderIf';
import './AssigneeCell.scss';

interface AssigneeCellProps {
    orgId: string;
    assignees: TimelineAssignee[];
    onAssign: (userCorrelationId: string) => void;
    onRemove: (userCorrelationId: string) => void;
    disabled?: boolean;
}

const AssigneeCell = ({
    orgId,
    assignees,
    onAssign,
    onRemove,
    disabled = false,
}: AssigneeCellProps) => {
    const { data: organizationUsers, isLoading } = useGetOrganizationUsers(orgId);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const toggleRef = useRef<HTMLDivElement>(null);
    const toggle = () => setDropdownOpen(prevState => !prevState);

    const assignedUserIds = useMemo(() => new Set(assignees.map(a => a.userCorrelationId)), [assignees]);

    // Split users into assigned and unassigned, sorted alphabetically
    const { assignedUsers, unassignedUsers } = useMemo(() => {
        if (!organizationUsers) return { assignedUsers: [], unassignedUsers: [] };

        const assigned = [];
        const unassigned = [];
        for (const user of organizationUsers) {
            if (assignedUserIds.has(user.correlationId)) assigned.push(user);
            else {
                const isDealAdminOrManager = user.dealRoleId && TIMELINE_ASSIGNEE_DEAL_ROLES.includes(user.dealRoleId);
                if (isDealAdminOrManager || user.userClass === USER_CLASSES.SUPER_ADMIN) unassigned.push(user);
            }
        }

        const sortByName = (a: IUser, b: IUser) => (a.name || a.email).localeCompare(b.name || b.email);
        assigned.sort(sortByName);
        unassigned.sort(sortByName);

        return { assignedUsers: assigned, unassignedUsers: unassigned };
    }, [organizationUsers, assignedUserIds]);

    return (
        <Dropdown isOpen={dropdownOpen} toggle={toggle} disabled={disabled} direction="down">
            <DropdownToggle
                tag="div"
                className="assignee-cell-toggle"
                innerRef={toggleRef}
            >
                <RenderIf isTrue={!assignees.length}>
                    <span>&nbsp;</span>
                </RenderIf>
                <RenderIf isTrue={assignees.length}>
                    <div className="d-flex flex-nowrap gap-1 assignee-badges-container">
                        {assignees.map(assignee => (
                            <div key={assignee.id} className="assignee-badge-readonly">
                                <span>{assignee.userName || 'Unknown'}</span>
                            </div>
                        ))}
                    </div>
                </RenderIf>
            </DropdownToggle>
            <DropdownMenu
                className="assignee-dropdown-menu"
                container="body"
                modifiers={{
                    preventOverflow: {
                        enabled: true,
                        boundariesElement: 'viewport'
                    },
                }}
            >
                <RenderIf isLoading={isLoading} isTrue={!assignedUsers.length && !unassignedUsers.length}>
                    <DropdownItem disabled>No users available</DropdownItem>
                </RenderIf>

                {/* Assigned users with checkmarks */}
                {assignedUsers.map(user => (
                    <DropdownItem
                        key={user.correlationId}
                        onClick={() => onRemove(user.correlationId)}
                        className="assignee-user-item"
                    >
                        <FontAwesomeIcon icon={faCheck} className="assignee-checkmark" />
                        <span>{user.name || user.email}</span>
                    </DropdownItem>
                ))}

                {/* Unassigned users */}
                {unassignedUsers.map(user => (
                    <DropdownItem
                        key={user.correlationId}
                        onClick={() => onAssign(user.correlationId)}
                        className="assignee-user-item"
                    >
                        <div className="checkmark-placeholder" />
                        <span>{user.name || user.email}</span>
                    </DropdownItem>
                ))}
            </DropdownMenu>
        </Dropdown>
    );
};

export default AssigneeCell;
